import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
	DrilldownState,
	isValidReferrer
} from 'src/apps/dashboard/components/accessibility-summary/helpers/drilldownLinkHelper';
import {
	ALL_ISSUES_TABLE_ID,
	UNIQUE_ISSUES_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import { handleDashboardRedirection } from 'src/apps/dashboard/helpers/NavigationHelpers';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isConfigurationReady, isDashboardEnabled } from 'src/common/helpers/ConfigurationHelpers';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { buildIssuesPageTabs, renderIssuesTableContent } from './helpers/IssuesPageHelpers';
import { ALL_ISSUES_COLUMNS, UNIQUE_ISSUES_COLUMNS } from './helpers/IssuesTableColumns';
import { useAllIssuesData } from './hooks/useAllIssuesData';
import { useFiltersWithQueryParams } from './hooks/useFiltersWithQueryParams';
import { useIssuesPageFilters } from './hooks/useIssuesPageFilters';
import { useUniqueIssuesData } from './hooks/useUniqueIssuesData';
import {
	EMPTY_DASHBOARD_QUERY,
	EMPTY_SCORE_QUERY,
	HEADER,
	ISSUES_PAGE_BOTTOM_KIND,
	ISSUES_PAGE_FILTER_PRESET,
	ISSUES_PAGE_TABS_ID
} from './IssuesPage.constants';
import { ISSUES_PAGE_LABELS } from './IssuesPage.labels';

import './IssuesPage.scss';

/**
 * Web Issues Page — drill-down view from the dashboard. Default export
 * keeps the historical page import path working.
 */
export const IssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();

	const { getTableStateById, updateTableState } = useTableState();
	const uniqueTableState = getTableStateById(UNIQUE_ISSUES_TABLE_ID);
	const allTableState = getTableStateById(ALL_ISSUES_TABLE_ID);

	usePlatformConfigInitializer();

	const { filterDefinitions, filterOptions } = useIssuesPageFilters();

	const { configuration } = useConfiguration();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();

	const { filters, handleFilterChange } = useFiltersWithQueryParams({
		filterDefinitions,
		filterOptions
	});
	const lastHandledSearchRef = useRef<string | null>(null);

	const resetTablesToFirstPage = useCallback(() => {
		updateTableState(ALL_ISSUES_TABLE_ID, {
			...allTableState,
			paginationInfo: { ...allTableState.paginationInfo, page: 1 }
		});

		updateTableState(UNIQUE_ISSUES_TABLE_ID, {
			...uniqueTableState,
			paginationInfo: { ...uniqueTableState.paginationInfo, page: 1 }
		});
	}, [allTableState, uniqueTableState, updateTableState]);

	const handleIssuesFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			resetTablesToFirstPage();
			handleFilterChange(newFilters);
		},
		[handleFilterChange, resetTablesToFirstPage]
	);

	useEffect(() => {
		if (lastHandledSearchRef?.current === location?.search) {
			return;
		}
		lastHandledSearchRef.current = location?.search ?? '';
		resetTablesToFirstPage();
	}, [location?.search, resetTablesToFirstPage]);

	const isUniqueFromUrl = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const activeTabIndex = isUniqueFromUrl ? 1 : 0;

	const {
		data: uniqueIssuesData,
		isFetching: isFetchingUnique,
		error: uniqueError
	} = useUniqueIssuesData(
		filters,
		uniqueTableState.paginationInfo.page,
		uniqueTableState.paginationInfo.sizePerPage,
		uniqueTableState.tableSort?.[0],
		isUniqueFromUrl
	);

	const {
		data: allIssuesData,
		isFetching: isFetchingAll,
		error: allError
	} = useAllIssuesData(
		filters,
		allTableState.paginationInfo.page,
		allTableState.paginationInfo.sizePerPage,
		allTableState.tableSort?.[0],
		!isUniqueFromUrl
	);

	const handleBackClick = useCallback((): void => {
		const { referrer } = (location.state as DrilldownState) ?? {};
		if (isValidReferrer(referrer)) {
			history.push(referrer);
		} else {
			history.push(RoutesHelper.getAccessibilityDashboardPath());
		}
	}, [history, location?.state]);

	const handleTabSelected = useCallback(
		(index: number) => {
			resetTablesToFirstPage();

			const searchParams = new URLSearchParams(location.search);
			if (index === 1) {
				searchParams.set(UNIQUE_QUERY_PARAM, 'true');
			} else {
				searchParams.delete(UNIQUE_QUERY_PARAM);
			}
			const newSearch = searchParams.toString();
			history.replace({
				pathname: location?.pathname,
				search: `?${newSearch ?? ''}`,
				state: location.state
			});
		},
		[history, location.pathname, location.search, location.state, resetTablesToFirstPage]
	);

	const { tabs, tabPanels } = useMemo(() => {
		const allTabContent = renderIssuesTableContent({
			columns: ALL_ISSUES_COLUMNS,
			isFetching: isFetchingAll,
			error: allError,
			data: allIssuesData,
			tableId: ALL_ISSUES_TABLE_ID,
			title: ISSUES_PAGE_LABELS.ALL_ISSUES_TABLE_TITLE,
			errorMessage: ISSUES_PAGE_LABELS.ALL_ISSUES_LOAD_ERROR
		});
		const uniqueTabContent = renderIssuesTableContent({
			columns: UNIQUE_ISSUES_COLUMNS,
			isFetching: isFetchingUnique,
			error: uniqueError,
			data: uniqueIssuesData,
			tableId: UNIQUE_ISSUES_TABLE_ID,
			title: ISSUES_PAGE_LABELS.UNIQUE_ISSUES_TABLE_TITLE,
			errorMessage: ISSUES_PAGE_LABELS.UNIQUE_ISSUES_LOAD_ERROR
		});

		return buildIssuesPageTabs({
			activeTabIndex,
			onTabSelected: handleTabSelected,
			allTabContent,
			uniqueTabContent
		});
	}, [
		activeTabIndex,
		handleTabSelected,
		isFetchingAll,
		allError,
		allIssuesData,
		isFetchingUnique,
		uniqueError,
		uniqueIssuesData
	]);

	const header = useMemo(
		() => ({
			...HEADER,
			backLink: { ...HEADER.backLink, onClick: handleBackClick }
		}),
		[handleBackClick]
	);

	const filter = useMemo(
		() => ({
			filters,
			filterDefinitions,
			filterOptions,
			onFilterChange: handleIssuesFilterChange,
			filterPreset: ISSUES_PAGE_FILTER_PRESET
		}),
		[filters, filterDefinitions, filterOptions, handleIssuesFilterChange]
	);

	const bottom = useMemo(
		() => ({
			kind: ISSUES_PAGE_BOTTOM_KIND,
			tabState: {
				tabs,
				tabPanels,
				activeTabIndex,
				onTabSelected: handleTabSelected,
				tabsId: ISSUES_PAGE_TABS_ID
			}
		}),
		[tabs, tabPanels, activeTabIndex, handleTabSelected]
	);

	if (!isConfigurationReady(configuration)) {
		return null;
	}

	if (!isDashboardEnabled(configuration)) {
		handleDashboardRedirection(history, configuration);
		return null;
	}

	return (
		<DashboardTemplate
			header={header}
			filter={filter}
			effectiveFilters={filters}
			scoreQuery={EMPTY_SCORE_QUERY}
			dashboardQuery={EMPTY_DASHBOARD_QUERY}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
